import type { Card, Suit } from "@fiveout/core";
import { getSelectedTheme, type CardTheme } from "../../theme/themes";
import { MAX_CARD_H } from "./layout";

/**
 * The handful of cards a screen transition deals: a spread out of the player's
 * own deck, drawn by the same `cardArt` builders the table uses.
 *
 * Fetched through a LAZY import, and that is the whole design of this file.
 * `pixi/cardPreview` is what renders a card to a data URL — memoised, one shared
 * offscreen context — but it imports Pixi, and `App.tsx` is code-split precisely
 * so the renderer arrives on the routes that need it rather than on the home
 * screen. A static import here would put the whole table renderer in front of
 * the first paint to decorate a navigation, which is a bad trade in both
 * directions.
 *
 * Three things follow from that, and they are what make a pack of FACES
 * affordable where a wall of identical backs was trivially cheap.
 */

/**
 * A fixed, deliberately varied hand — not a random draw.
 *
 * The cards are the same every session so they can be rendered once and cached;
 * what is random is which of them lands where, and that is decided per
 * transition by the layout's own seeded RNG. Sampling the deck freshly each time
 * would mean rendering new images on every navigation, for a difference nobody
 * can see in a second and a half of motion.
 *
 * Values are spread across the whole 1–13 range rather than clustered, suits
 * cycle so all four show, and every wild is in: a spread of eight-through-tens
 * in one color reads as a texture, not as a deck.
 */
const HAND: readonly Card[] = buildHand();

function buildHand(): Card[] {
  const suits: Suit[] = ["diamonds", "spades", "hearts", "clubs"];
  // Every value once, then five more in a different suit — enough distinct faces
  // that a pack of fifty-odd does not read as a tiling of the same six cards.
  const values = [1, 7, 12, 4, 9, 2, 13, 6, 10, 3, 11, 5, 8, 10, 4, 13, 6, 9];
  const cards: Card[] = values.map((value, i) => ({
    id: `tx-${value}-${i}`,
    kind: "value",
    value,
    suit: suits[i % suits.length]!,
    deckIndex: 0,
  }));
  // Every wild, because they are the cards in the deck that look like nothing
  // else in it and a spread without them is missing its punctuation.
  cards.push({ id: "tx-smash", kind: "wild", wildIndex: 0, wildKind: "smash", deckIndex: 0 });
  cards.push({ id: "tx-lightning", kind: "wild", wildIndex: 1, wildKind: "lightning", deckIndex: 0 });
  cards.push({ id: "tx-swap", kind: "wild", wildIndex: 2, wildKind: "swap", deckIndex: 0 });
  cards.push({ id: "tx-peek", kind: "wild", wildIndex: 3, wildKind: "peek", deckIndex: 0 });
  return cards;
}

/** How many distinct faces there are to deal. The layout indexes into this. */
export const HAND_SIZE = HAND.length;

/**
 * The five cards the home screen fans out (`HomeHand`), as indices into HAND:
 * K of hearts, 7 of spades, the 5 of clubs in the middle - it is Five Out - a
 * Lightning for the one card nobody mistakes for a plain deck, and the ace of
 * diamonds. Kept here, beside the hand, because it means nothing without it.
 */
export const HOME_HAND: readonly number[] = [6, 1, 11, 19, 0];

/**
 * Roughly how much of a spread lands face down.
 *
 * Not zero, for two reasons. A real pack tossed on a table has some of both, and
 * — the practical half — the back is the one image that is always ready, so it
 * is what a face that has not finished rendering falls back to. A transition
 * during the first seconds of a session is therefore a mostly face-down pack
 * that fills in with faces, rather than a grid of blank swatches.
 */
export const BACK_SHARE = 0.25;

const CARD_ART_HEIGHT = 110;

/**
 * How many device pixels per card unit the printed cards need.
 *
 * `renderCardImage` defaults to the SCREEN's pixel ratio, which is right for a
 * preview drawn at about card size and wrong here: a transition prints a card up
 * to `MAX_CARD_H` tall, so on an ordinary 1x desktop the default handed this a
 * 78x110 image to stretch across three times its width. That is the entire
 * reason the effect looked soft on a desktop and fine on a phone — the phone's
 * own 3x ratio was quietly covering for it.
 */
function cardResolution(): number {
  const dpr = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
  return Math.max(2, Math.ceil((MAX_CARD_H * dpr) / CARD_ART_HEIGHT));
}

/**
 * The pack the overlay is drawing: whichever deck was last rendered COMPLETE
 * enough to show, plus the faces filled in behind it as they arrive.
 *
 * The reason this is a swap rather than a mutable set is a real bug. Priming
 * used to null everything out the moment it noticed a different deck, and the
 * accessors used to refuse to answer for a theme that was not the current one —
 * so the very next transition after a player changed their cards drew a screen
 * of blank rectangles, every time, until the new back had rendered. Holding the
 * OLD deck for the moment it takes to render the new one is strictly better: it
 * is the pack they were just looking at, rather than no pack at all.
 */
interface Pack {
  themeId: string;
  back: string;
  faces: (string | null)[];
}

let shown: Pack | null = null;
/** The deck currently being rendered, if any. Swapped into `shown` once its back exists. */
let building: string | null = null;

/**
 * The back of the pack on screen, or null before anything at all has rendered.
 *
 * Deliberately NOT gated on the answer matching the player's current selection:
 * see `Pack`. A deck change is a swap, not a blanking.
 */
/**
 * Tells `listener` whenever the pack changes - a new back swapped in, or one
 * more face rendered. Returns the unsubscribe.
 *
 * For the home screen's hand (`HomeHand`), which shows faces from this same
 * pack as they arrive rather than rendering a second set of its own: the pack is
 * already being built at launch for the transitions, and a second renderer run
 * for five more cards would be the very readback cost this module paces.
 */
const listeners = new Set<() => void>();
export function onCardImages(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
function notify(): void {
  for (const l of listeners) l();
}

export function cardBackUrl(): string | null {
  return shown?.back ?? null;
}

/**
 * The face at `index` of the pack on screen, or null if that one has not been
 * rendered yet. Callers fall back to the back, and then to the deck's colors.
 */
export function cardFaceUrl(index: number): string | null {
  return shown?.faces[index] ?? null;
}

/**
 * Renders the current deck's pack if it is not already the one on screen.
 *
 * Called from three places, and the first is the one that matters most:
 *
 *  - **at launch**, on idle, so the very first transition of a session is
 *    already dressed. Without it the first navigation drew the color swatch,
 *    because nothing had asked for a card yet;
 *  - **when the player changes their deck**, so the swap is ready before they
 *    navigate away from the picker;
 *  - **at the start of every transition**, which is the backstop for both.
 *
 * Cheap to call repeatedly: it returns at once unless the current deck is
 * neither on screen nor already being built, and `renderCardImage` memoises per
 * theme, card and resolution underneath. Deliberately returns nothing — a
 * transition never waits on this, because a navigation that waited on a texture
 * would be a navigation that hangs when WebGL is unavailable.
 *
 * **One card at a time, and the back first.** Each render is a GPU readback plus
 * a base64 encode, and firing twenty of those off together drops frames at
 * exactly the moment the cards are supposed to be flying. Sequential means the
 * pack fills in over a second or two — and because an unrendered face falls back
 * to the back, a half-primed hand looks like a pack with some cards face down
 * rather than like a bug.
 */
export function primeCardImages(): void {
  const theme = getSelectedTheme();
  if (shown?.themeId === theme.id || building === theme.id) return;
  building = theme.id;
  void run(theme).finally(() => {
    if (building === theme.id) building = null;
  });
}

async function run(theme: CardTheme): Promise<void> {
  const resolution = cardResolution();
  let renderCardImage: (typeof import("../../pixi/cardPreview"))["renderCardImage"];
  try {
    ({ renderCardImage } = await import("../../pixi/cardPreview"));
  } catch {
    // The chunk failed to load. The deck's own colors are a perfectly good
    // stand-in and the transition goes ahead regardless.
    return;
  }

  // The back first, and nothing is swapped until it exists: it is what every
  // unrendered face falls back to, so it is the one image whose absence would
  // be visible as a blank card.
  const back = await renderCardImage(theme, { kind: "back" }, resolution).catch(() => null);
  if (!back) return;
  // A player who changed decks twice quickly: the pack being built is no longer
  // the one they want, so drop it rather than showing a deck they have left.
  if (getSelectedTheme().id !== theme.id) return;
  const pack: Pack = { themeId: theme.id, back, faces: new Array(HAND_SIZE).fill(null) };
  shown = pack;
  notify();

  for (let i = 0; i < HAND.length; i++) {
    // A breath between readbacks, so a burst of them cannot land inside one
    // frame of an animation that is already running.
    await nextIdle();
    // Written into the pack rather than into `shown`, so faces arriving late for
    // a deck that has since been replaced land somewhere harmless.
    pack.faces[i] = (await renderCardImage(theme, { kind: "face", card: HAND[i]! }, resolution).catch(() => null)) ?? null;
    if (pack === shown) notify();
  }
}

/** Yields to the browser, preferring genuine idle time where it is offered. */
function nextIdle(): Promise<void> {
  return new Promise((resolve) => {
    const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback;
    // A short timeout on purpose: long enough that a busy frame is never made
    // busier, short enough that the hand is dealt within a couple of navigations
    // rather than a couple of minutes.
    if (ric) ric(() => resolve(), { timeout: 80 });
    else setTimeout(resolve, 16);
  });
}

/** The two colors a card of this deck is printed in, for the fallback swatch. */
export function cardBackColors(theme: CardTheme = getSelectedTheme()): { primary: string; secondary: string } {
  return { primary: theme.primary, secondary: theme.secondary };
}
